/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomerRegistrationTypeChoosing from './CustomerRegistrationTypeChoosing';
import PersonalRegistration from './Personal';
import BusinessRegistration from './Business';
import VerificationCode from './VerificationCode';
import {
  REGISTRATION_CUSTOMER_TYPE,
  CUSTOMER_TYPE,
  REGISTRATION_STATUSES,
  REGISTRATION_STEPS
} from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import Alert from 'uikit/Alert/Alert';
import { getErrorMessageForAlert } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { isTokenValid } from 'services/authUtils';
import '../RegistrationStyles.scss';

const Registration = ({ registrationStore, userStore }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const i18n = useContext(i18nContext);
  const personalCustomerRegistrationInvitationCode =
    process.env.REACT_APP_PERSONAL_CUSTOMER_REGISTRATION_INVITATION_CODE;

  const isUserLoggedIn = isTokenValid();

  // Check if user has at least one customer account
  const hasCustomerAccounts = useMemo(() => {
    return isUserLoggedIn && userStore?.userData?.availableAccounts?.length > 0;
  }, [isUserLoggedIn, userStore?.userData?.availableAccounts]);

  // Check if user has any INDIVIDUAL type customer
  const hasIndividualCustomer = useMemo(() => {
    if (!hasCustomerAccounts) return false;
    return userStore.userData.availableAccounts.some((account) => account.type === CUSTOMER_TYPE.INDIVIDUAL);
  }, [hasCustomerAccounts, userStore?.userData?.availableAccounts]);

  // Determine if invite code field should be shown
  const shouldShowInviteCode = useMemo(() => {
    // Hide invite code if user is logged in, has customers, but no individual customer
    if (isUserLoggedIn && hasCustomerAccounts && !hasIndividualCustomer) {
      return false;
    }
    // Show invite code in all other cases
    return true;
  }, [isUserLoggedIn, hasCustomerAccounts, hasIndividualCustomer]);

  // Sync type with route
  useEffect(() => {
    if (location.pathname === ROUTE_PATHS.PERSONAL_REGISTRATION) {
      // Allow logged-in users with customers but no individual to proceed without invite code
      const canProceedWithoutCode = isUserLoggedIn && hasCustomerAccounts && !hasIndividualCustomer;

      if (!canProceedWithoutCode && registrationStore.inviteCode !== personalCustomerRegistrationInvitationCode) {
        navigate(ROUTE_PATHS.REGISTRATION_CUSTOMER);
      } else {
        registrationStore.setRegistrationType(REGISTRATION_CUSTOMER_TYPE.PERSONAL);
      }
    } else if (location.pathname === ROUTE_PATHS.COMPANY_REGISTRATION) {
      registrationStore.setRegistrationType(REGISTRATION_CUSTOMER_TYPE.BUSINESS);
    }
  }, [location.pathname, isUserLoggedIn, hasCustomerAccounts, hasIndividualCustomer]);

  // If user did not pass step 2 yet and is not logged in, store preference and redirect to /registration
  useEffect(() => {
    if (
      location.pathname === ROUTE_PATHS.PERSONAL_REGISTRATION ||
      location.pathname === ROUTE_PATHS.COMPANY_REGISTRATION ||
      location.pathname === ROUTE_PATHS.REGISTRATION_CUSTOMER
    ) {
      if (!isTokenValid() && registrationStore.currentRegistrationStep !== REGISTRATION_STEPS.CUSTOMER_CREATION) {
        navigate(ROUTE_PATHS.REGISTRATION, { replace: true });
      } else {
        if (
          registrationStore.currentRegistrationStep !== REGISTRATION_STEPS.CUSTOMER_CREATION_CONFIRMATION &&
          registrationStore.currentRegistrationStep !== REGISTRATION_STEPS.REGISTRATION_COMPLETED
        ) {
          registrationStore.setCurrentRegistrationStep(REGISTRATION_STEPS.CUSTOMER_CREATION);
        }
      }
    }
  }, [location.pathname, registrationStore.currentRegistrationStep]);

  // Navigate to success only when customer registration flow is completed
  useEffect(() => {
    registrationStore.clearError();
    if (registrationStore.currentRegistrationStep === REGISTRATION_STEPS.REGISTRATION_COMPLETED) {
      navigate(ROUTE_PATHS.REGISTRATION_SUCCESS);
    }
  }, [registrationStore.currentRegistrationStep]);

  // Do not render step 3 form until we are at the correct step (or email confirmation) for unauthenticated flow
  if (
    !isTokenValid() &&
    registrationStore.currentRegistrationStep !== REGISTRATION_STEPS.CUSTOMER_CREATION &&
    registrationStore.registrationStatus !== REGISTRATION_STATUSES.REGISTRATION_STATUS_EMAIL_CONFIRMATION
  ) {
    return null;
  }

  return registrationStore.registrationStatus === REGISTRATION_STATUSES.REGISTRATION_STATUS_EMAIL_CONFIRMATION ? (
    <VerificationCode />
  ) : (
    <div className={'registration-form'} id={'form1'}>
      <h1 className={'registration-title'}>
        {i18n.getMessage(
          location.pathname === ROUTE_PATHS.REGISTRATION_CUSTOMER
            ? 'register.title.chooseYourAccountType'
            : registrationStore.registrationType === REGISTRATION_CUSTOMER_TYPE.PERSONAL
              ? 'register.title.createIndividualCustomer'
              : 'register.title.createBusinessCustomer'
        )}
      </h1>
      <Alert
        type={'warning'}
        message={registrationStore?.error && getErrorMessageForAlert(i18n, registrationStore.error)}
      />
      {location.pathname === ROUTE_PATHS.REGISTRATION_CUSTOMER ? (
        <CustomerRegistrationTypeChoosing
          isUserLoggedIn={isUserLoggedIn}
          hasCustomerAccounts={hasCustomerAccounts}
          hasIndividualCustomer={hasIndividualCustomer}
          shouldShowInviteCode={shouldShowInviteCode}
        />
      ) : (
        <div className={'form-data'}>
          {registrationStore.registrationType === REGISTRATION_CUSTOMER_TYPE.PERSONAL ? (
            <PersonalRegistration />
          ) : (
            <BusinessRegistration />
          )}
        </div>
      )}
    </div>
  );
};

Registration.propTypes = {
  registrationStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  registrationStore: stores.registrationStore,
  userStore: stores.userStore
}))(observer(Registration));
